import { ApplicationState } from '@/store';
import { UserRole } from '@/store/auth/types';
import { Badge, BadgeProps, Box, Flex } from '@chakra-ui/react';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Span } from 'shared-components';
import { Agent, AgentApplicantAuthorisationStatus, EApplicationPGKeys } from 'api-paths-cas';
import { TableCellProps } from '../../../types';
import { defaultHeaderRenderer } from '../../headers';
import { IColumn } from '../../types';

interface AgentAuthorisationStatusProps {
  agentAccessStatus: string;
}

const AgentAccessBadge: React.FC<React.PropsWithChildren<BadgeProps>> = props => {
  return (
    <Badge {...props} maxWidth={'100%'}>
      <Span overflow={'hidden'} textOverflow={'ellipsis'}>
        {props.children}
      </Span>
    </Badge>
  );
};

const AgentAuthorisationStatus: React.FC<AgentAuthorisationStatusProps> = ({ agentAccessStatus }) => {
  let statusMsg: React.ReactElement | null = null;
  switch (agentAccessStatus) {
    case AgentApplicantAuthorisationStatus.AUTHORISATION_PENDING:
      statusMsg = <AgentAccessBadge>Pending authorisation</AgentAccessBadge>;
      break;
    case AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED:
      statusMsg = (
        <AgentAccessBadge colorScheme={'red'} variant={'solid'}>
          Authorisation denied
        </AgentAccessBadge>
      );
      break;
  }
  return statusMsg;
};

const Formatter = memo(function formatter(cellProps: TableCellProps) {
  const { agentId } = cellProps.row;
  const agentDetails = useSelector((state: ApplicationState) =>
    state.agencies.list.find(agent => agent.id === agentId),
  );

  return (
    <Flex height="100%" width="100%" flexDirection={'column'}>
      {agentDetails?.displayName ? (
        <>
          <Box as="span">{agentDetails?.displayName}</Box>
          <AgentAuthorisationStatus agentAccessStatus={cellProps.row.agentAccessStatus} />
        </>
      ) : (
        <Box as="span">{Agent.None}</Box>
      )}
    </Flex>
  );
});

export const AgentColumn = (): IColumn => {
  return {
    item: {
      key: EApplicationPGKeys.AGENT_ID,
      name: 'Agent',
      width: 'minmax(175px, .75fr)',
      headerRenderer: defaultHeaderRenderer,
      sortable: true,
      responsive: true,
      formatter: Formatter,
    },
    visible: (state: { role: UserRole }) => {
      // Get the user role from the state object passed to visible function
      const userRole = state?.role;

      // Hide column for Agent and AgencyAdmin users
      return userRole !== UserRole.Agent && userRole !== UserRole.AgencyAdmin;
    },
  };
};
